import { Conversation, Message, Participant, User } from '@twilio/conversations';
import AppSettings from 'src/app/common/app.settings';
import { UserInfo } from 'src/app/interfaces';
import { EntityStatus, ItemStatus } from 'src/app/interfaces/views.interface';
import { AccountStatus, Conversation as ConversationBasic, UserBasic } from 'src/app/swagger-api';
import { getStatus } from '../../profile/user-profile/view-profile/regular-user-profile/regular-user-profile-helper';
import { ChatChannelType, ChatConversation } from '../interfaces/chat-model.interface';
import { TwilioConversationAttributes, TwilioUserAttributes } from '../services/twilio/twilio.interfaces';
import { getChannelType, getInterlocutor } from './chat.helpers';
import { TwilioMessagesAdapter } from './twilio-message.adapter';
import { computeConversationEffectiveTitle } from './twilio-message.helpers';

export class ConversationsAdapter {
  public static adaptToInternalConversation(currentUserId: string, conversation: ConversationBasic): ChatConversation {
    const interlocutor: UserBasic | null =
      conversation.participants.length > 2 ? null : getInterlocutor(conversation, currentUserId);
    const participatingUsers: UserInfo[] = conversation.participants.map(ConversationsAdapter.adaptUserBasic2UserInfo);
    return {
      relatedEntityId: conversation.entity_id,
      effectiveTitle: computeConversationEffectiveTitle(conversation.title, participatingUsers, currentUserId),
      originalTitle: conversation.title,
      participatingUsers: participatingUsers.sort((a, b) => a.userName.localeCompare(b.userName)),
      channel: conversation.messaging_channel_basic,
      channelType: ChatChannelType.CONVERSATION,
      lastMessage: null,
      imageUrl: this.getUserImage(interlocutor),
    };
  }

  public static async adaptTwilioConversationToConversation(
    currentUserId: string,
    twilioConversation: Conversation,
    userBlockedbyCurrentUser: string[],
    lastMessage?: Message,
  ): Promise<ChatConversation> {
    const channelParticipants: Participant[] = await twilioConversation.getParticipants();
    const channelUsers: UserInfo[] = await ConversationsAdapter.adaptTwilioParticipantsToUsersInfo(
      channelParticipants,
      userBlockedbyCurrentUser,
    );

    let conversationImage: string = AppSettings.DEFAULT_USER_IMAGE;
    let lastMessageSenderInfo: UserInfo | null = null;

    if (lastMessage) {
      const lastMessageSenderIndex: number = channelUsers.findIndex(
        (user: UserInfo) => user.userId === lastMessage.author,
      );
      if (lastMessageSenderIndex !== -1) {
        conversationImage = channelUsers[lastMessageSenderIndex].userImage;
        lastMessageSenderInfo = channelUsers[lastMessageSenderIndex];
      }
    }

    const channelType: ChatChannelType = getChannelType(twilioConversation);
    if (channelType === ChatChannelType.CONVERSATION && channelParticipants.length === 2) {
      const interlocutorIndex: number = channelUsers.findIndex((user: UserInfo) => user.userId !== currentUserId);
      if (interlocutorIndex !== -1) {
        conversationImage = channelUsers[interlocutorIndex].userImage;
      }
    }

    return {
      relatedEntityId: (twilioConversation.attributes as TwilioConversationAttributes).channel_id,
      effectiveTitle: computeConversationEffectiveTitle(twilioConversation.friendlyName, channelUsers, currentUserId),
      originalTitle: twilioConversation.friendlyName,
      participatingUsers: channelUsers.sort((a, b) => a.userName.localeCompare(b.userName)),
      lastMessage: lastMessage
        ? TwilioMessagesAdapter.adaptTwilioMessageToCustomMessage(
            lastMessage,
            channelParticipants,
            lastMessageSenderInfo,
          )
        : null,
      imageUrl: conversationImage && channelUsers.length === 2 ? conversationImage : AppSettings.DEFAULT_USER_IMAGE,
      channel: {
        service_name: 'Twilio',
        channel_external_id: twilioConversation.uniqueName,
        readonly: false,
      },
      channelType,
    };
  }

  public static privateConversationImage(channelUsers: UserInfo[], currentUserId: string): string {
    const currentUser: UserInfo = channelUsers.find((user: UserInfo) => user.userId !== currentUserId);
    return currentUser ? currentUser.userImage : '';
  }

  private static getUserImage(user: UserBasic | null): string {
    return user && user.image_url ? user.image_url : AppSettings.DEFAULT_USER_IMAGE;
  }

  public static async adaptTwilioParticipantsToUsersInfo(
    participants: Participant[],
    userBlockedbyCurrentUser: string[],
  ): Promise<UserInfo[]> {
    const users: User[] = await Promise.all(participants.map((p: Participant) => p.getUser()));
    return users.map((user: User) => this.adaptTwilioUserToUserInfo(user, userBlockedbyCurrentUser));
  }

  public static adaptTwilioUserToUserInfo(user: User, userBlockedbyCurrentUser: string[]): UserInfo {
    const userAttributes: TwilioUserAttributes = user.attributes as TwilioUserAttributes;
    let userStatus: EntityStatus = AccountStatus.ACTIVE;

    if (userBlockedbyCurrentUser.includes(user.identity)) userStatus = ItemStatus.BLOCKED;
    if (userAttributes.deactivated) userStatus = AccountStatus.DEACTIVATED;
    if (userAttributes.deleted) userStatus = AccountStatus.DELETED;

    return {
      userId: user.identity,
      userName: user.friendlyName,
      userImage: userAttributes.image_url ? userAttributes.image_url : AppSettings.DEFAULT_USER_IMAGE,
      userStatus,
    };
  }

  public static adaptUserBasic2UserInfo(userBasic: UserBasic): UserInfo {
    return {
      userId: userBasic.user_id,
      userName: userBasic.displayed_name,
      userImage: userBasic.image_url,
      userStatus: getStatus(userBasic),
    } as UserInfo;
  }
}
